import {
  PlatformFeedbackData,
  PlatformFeedbackType,
  ReviewData,
  ReviewService,
} from "./review-service";
import {
  captureMonitoringException,
  setMonitoringContext,
} from "./sentry";

const STEPS = [
  {
    id: "rating",
    title: "Como fue su experiencia?",
    subtitle: "Califique la atencion recibida",
  },
  {
    id: "punctuality",
    title: "El medico fue puntual?",
    subtitle: "Cuentenos sobre la puntualidad",
  },
  {
    id: "comment",
    title: "Algo mas que compartir?",
    subtitle: "Tu comentario es opcional",
  },
];

export class ReviewUI {
  private container: HTMLElement;
  private reviewService: ReviewService;
  private appointmentId: string;
  private currentStep = 0;
  private selectedRating = 0;
  private selectedPunctuality = "";
  private comment = "";
  private platformFeedback: PlatformFeedbackData = {
    npsScore: null,
    feedbackType: undefined,
    comment: "",
  };

  constructor(appointmentId: string, token: string, apiUrl: string) {
    this.appointmentId = appointmentId;
    this.reviewService = new ReviewService(apiUrl, token);
    this.container = document.getElementById("app-container")!;
    setMonitoringContext({ page: "review", appointmentId });
    this.render();
  }

  private render() {
    this.container.innerHTML = `
      <div class="review-page">
        <div class="review-container">
          <div class="review-brand-header">
            <h2>Su consulta ha finalizado</h2>
          </div>

          <div class="review-content">
            <div class="step-progress">
              ${STEPS.map(
                (_, i) => `
                <div class="step-dot ${i < this.currentStep ? "done" : i === this.currentStep ? "active" : ""}"></div>
              `,
              ).join("")}
            </div>

            <div class="review-header">
              <h1>${STEPS[this.currentStep].title}</h1>
              <p class="review-subtitle">${STEPS[this.currentStep].subtitle}</p>
            </div>

            <div class="step-content">
              ${this.renderStep()}
            </div>

            <div class="step-actions">
              ${this.currentStep > 0 ? `<button class="back-button" id="back-btn">Atras</button>` : "<div></div>"}
              <button class="next-button" id="next-btn">
                ${this.currentStep === STEPS.length - 1 ? "Enviar calificacion" : "Continuar"}
              </button>
            </div>

            <div class="error-message" id="step-error"></div>
          </div>
        </div>
      </div>
    `;

    this.attachStepListeners();
  }

  private renderStep(): string {
    switch (this.currentStep) {
      case 0:
        return `
          <div class="star-rating" id="star-rating">
            ${[1, 2, 3, 4, 5]
              .map(
                (v) => `
              <button type="button" class="star ${v <= this.selectedRating ? "selected" : ""}" data-value="${v}">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
                </svg>
              </button>
            `,
              )
              .join("")}
          </div>
          <div class="rating-label" id="rating-label">${this.getRatingLabel(this.selectedRating)}</div>
        `;

      case 1:
        return `
          <div class="punctuality-options">
            ${[
              { value: "ON_TIME", icon: "OK", text: "A tiempo" },
              { value: "LATE", icon: "T", text: "Con retraso" },
              { value: "NO_SHOW", icon: "X", text: "No se presento" },
            ]
              .map(
                (opt) => `
              <label class="punctuality-option">
                <input type="radio" name="punctualityRating" value="${opt.value}" ${this.selectedPunctuality === opt.value ? "checked" : ""}>
                <span class="option-content ${this.selectedPunctuality === opt.value ? "selected" : ""}">
                  <span class="option-icon">${opt.icon}</span>
                  <span class="option-text">${opt.text}</span>
                </span>
              </label>
            `,
              )
              .join("")}
          </div>
          <p class="skip-hint">Este paso es opcional</p>
        `;

      case 2:
        return `
          <div class="review-form-section">
            <label class="form-label" for="comment">Comentario sobre la consulta</label>
            <textarea
              id="comment"
              rows="5"
              class="form-textarea"
              placeholder="Comparte tu experiencia con la consulta..."
              maxlength="500"
            >${this.comment}</textarea>
            <div class="char-count"><span id="char-count">${this.comment.length}</span>/500</div>
          </div>

          <div class="platform-feedback-section">
            <div class="platform-feedback-header">
              <h2>Feedback sobre la plataforma</h2>
              <p>Estos campos son opcionales y nos ayudan a mejorar la experiencia.</p>
            </div>

            <div class="review-form-section">
              <label class="form-label" for="platform-nps">¿Qué probabilidad hay de que recomiende nuestro servicio a un amigo o familiar?</label>
              <input
                id="platform-nps"
                type="number"
                min="0"
                max="10"
                step="1"
                class="form-input"
                placeholder="0 a 10"
                value="${this.platformFeedback.npsScore ?? ""}"
              />
            </div>

            <div class="review-form-section">
              <label class="form-label" for="platform-feedback-type">Tipo de feedback</label>
              <select id="platform-feedback-type" class="form-select">
                <option value="">Seleccionar</option>
                ${this.renderPlatformFeedbackOption("GENERAL_COMMENT", "Comentario general")}
                ${this.renderPlatformFeedbackOption("BUG_REPORT", "Reportar un error")}
                ${this.renderPlatformFeedbackOption("FEATURE_REQUEST", "Sugerir una mejora")}
              </select>
            </div>

            <div class="review-form-section">
              <label class="form-label" for="platform-comment">Comentario sobre la plataforma</label>
              <textarea
                id="platform-comment"
                rows="4"
                class="form-textarea"
                placeholder="Cuentanos si algo fallo o que podriamos mejorar..."
                maxlength="500"
              >${this.platformFeedback.comment}</textarea>
              <div class="char-count"><span id="platform-char-count">${this.platformFeedback.comment.length}</span>/500</div>
            </div>
          </div>
        `;

      default:
        return "";
    }
  }

  private renderPlatformFeedbackOption(
    value: PlatformFeedbackType,
    label: string,
  ): string {
    const selected = this.platformFeedback.feedbackType === value ? "selected" : "";
    return `<option value="${value}" ${selected}>${label}</option>`;
  }

  private getRatingLabel(rating: number): string {
    const labels = ["", "Muy malo", "Malo", "Regular", "Bueno", "Excelente"];
    return labels[rating] ?? "";
  }

  private attachStepListeners() {
    const nextBtn = document.getElementById("next-btn")!;
    const backBtn = document.getElementById("back-btn");
    const errorEl = document.getElementById("step-error")!;

    backBtn?.addEventListener("click", () => {
      this.currentStep--;
      this.render();
    });

    nextBtn.addEventListener("click", async () => {
      errorEl.textContent = "";

      if (this.currentStep === 0) {
        if (!this.selectedRating) {
          errorEl.textContent = "Por favor, selecciona una calificacion";
          return;
        }
        this.currentStep++;
        this.render();
        return;
      }

      if (this.currentStep === 1) {
        const checked = document.querySelector<HTMLInputElement>(
          'input[name="punctualityRating"]:checked',
        );
        this.selectedPunctuality = checked?.value ?? "";
        this.currentStep++;
        this.render();
        return;
      }

      if (this.currentStep === 2) {
        const textarea = document.getElementById("comment") as HTMLTextAreaElement | null;
        this.comment = textarea?.value.trim() ?? "";
        this.platformFeedback = this.readPlatformFeedback();
        await this.submit(nextBtn as HTMLButtonElement, errorEl);
      }
    });

    if (this.currentStep === 0) {
      const stars = document.querySelectorAll<HTMLElement>(".star");
      const label = document.getElementById("rating-label")!;

      const updateStars = (rating: number) => {
        stars.forEach((s, i) => s.classList.toggle("selected", i < rating));
        label.textContent = this.getRatingLabel(rating);
      };

      stars.forEach((star) => {
        star.addEventListener("click", () => {
          this.selectedRating = parseInt(star.dataset.value!, 10);
          updateStars(this.selectedRating);
        });
        star.addEventListener("mouseenter", () => {
          updateStars(parseInt(star.dataset.value!, 10));
        });
      });

      document.getElementById("star-rating")!.addEventListener("mouseleave", () => {
        updateStars(this.selectedRating);
      });
    }

    if (this.currentStep === 2) {
      const textarea = document.getElementById("comment") as HTMLTextAreaElement | null;
      const charCount = document.getElementById("char-count");
      const platformTextarea = document.getElementById("platform-comment") as HTMLTextAreaElement | null;
      const platformCharCount = document.getElementById("platform-char-count");

      textarea?.addEventListener("input", () => {
        if (charCount) {
          charCount.textContent = textarea.value.length.toString();
        }
      });

      platformTextarea?.addEventListener("input", () => {
        if (platformCharCount) {
          platformCharCount.textContent = platformTextarea.value.length.toString();
        }
      });
    }
  }

  private readPlatformFeedback(): PlatformFeedbackData {
    const npsInput = document.getElementById("platform-nps") as HTMLInputElement | null;
    const feedbackTypeSelect = document.getElementById("platform-feedback-type") as HTMLSelectElement | null;
    const commentTextarea = document.getElementById("platform-comment") as HTMLTextAreaElement | null;
    const rawValue = npsInput?.value?.trim() ?? "";
    const parsedScore = rawValue ? Number(rawValue) : null;

    const npsScore =
      typeof parsedScore === "number" &&
      !Number.isNaN(parsedScore) &&
      parsedScore >= 0 &&
      parsedScore <= 10
        ? parsedScore
        : null;

    return {
      npsScore,
      feedbackType: (feedbackTypeSelect?.value || undefined) as PlatformFeedbackType | undefined,
      comment: commentTextarea?.value ?? "",
    };
  }

  private async submit(btn: HTMLButtonElement, errorEl: HTMLElement) {
    const reviewData: ReviewData = {
      appointmentId: this.appointmentId,
      overallScore: this.selectedRating,
    };

    if (this.comment) {
      reviewData.comment = this.comment;
    }

    if (this.selectedPunctuality) {
      const map: Record<string, number> = { ON_TIME: 5, LATE: 3, NO_SHOW: 1 };
      reviewData.punctualityScore = map[this.selectedPunctuality];
    }

    const hasPlatformFeedback =
      this.platformFeedback.npsScore !== null ||
      Boolean(this.platformFeedback.feedbackType) ||
      Boolean(this.platformFeedback.comment.trim());

    if (hasPlatformFeedback) {
      reviewData.platformFeedback = {
        npsScore: this.platformFeedback.npsScore ?? null,
        feedbackType: this.platformFeedback.feedbackType ?? "GENERAL_COMMENT",
        comment: this.platformFeedback.comment.trim(),
      };
    }

    try {
      btn.disabled = true;
      btn.textContent = "Enviando...";

      await this.reviewService.submitReview(reviewData);
      this.showSuccess();
    } catch (error: unknown) {
      captureMonitoringException(error, {
        feature: "review-ui-submit",
        appointmentId: this.appointmentId,
      });
      const message =
        error instanceof Error
          ? error.message
          : "Error al enviar. Por favor, intenta de nuevo.";
      errorEl.textContent = message;
      btn.disabled = false;
      btn.textContent = "Enviar calificacion";
    }
  }

  private showSuccess() {
    this.container.innerHTML = `
      <div class="review-page">
        <div class="review-container">
          <div class="review-brand-header">
            <h2>Su consulta ha finalizado</h2>
          </div>

          <div class="review-content">
            <div class="success-message">
              <div class="success-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                </svg>
              </div>
              <h1>Gracias por tu calificacion.</h1>
              <p class="success-subtitle">Tu opinion nos ayuda a seguir mejorando</p>
              <button class="close-button" onclick="window.close()">Cerrar ventana</button>
            </div>
          </div>
        </div>
      </div>
    `;
  }
}
